import { DocumentFileType, DocumentType } from 'types/types'
import { AddressForm, ContactField, WithContactStrings } from 'types/interfaces'
import { compact, forEach, forOwn, isArrayLikeObject, round } from "lodash";
import { Calendar } from 'calendar'
import { DateTime } from 'luxon'
import API from "./API";
import Nurse from "./Nurse";

export async function getStripeURL(api: API, nurse: Nurse) {
  switch (nurse.stripe_step) {
    case 'COMPLETED':
    case 'SUBMITTED':
      return await api.getStripeDashboardURL(nurse.nurse_id)
    case 'PROCESSING':
    case 'NOT_STARTED':
    default:
      return await api.getStripeConnectURL(nurse.nurse_id)
  }
}

export function fromKmToMiles(value: string | number, precision: number = 2) {
  if (value === '') return null
  return round(Number(value) * 0.621371, precision)
}

export function fromMilesToKm(value: string | number, precision: number = 2) {
  if (value === '') return null
  return round(Number(value) * 1.609, precision)
}

export function getEmptyCalendar(year: number, month: number) {
  const cal = new Calendar(1)
  const weeks = cal.monthDays(year, month - 1) // luxon January is 1, but JSDate January is 0
  const retval: { [key: string]: any } = {}
  weeks.forEach((week, weeknum) => {
    week.forEach((day, weekdaynum) => {
      const key = `${weeknum}-${weekdaynum}`
      const date = day ? DateTime.fromObject({ year, month, day }) : null
      retval[key] = {
        'date': date ? date.toFormat('yyyy-LL-dd') : '',
        'weeknum': weeknum,
        'weekdaynum': weekdaynum
      }
    })
  })
  return retval
}

export function downloadFile(blob: string) {
  const data = "data:text/csv;charset=utf-8," + blob
  const encodedUri = encodeURI(data)
  window.open(encodedUri)
}

export function joinAddressParts(form: AddressForm) {
  return [form.line1, form.line2, form.city, form.county, form.country, form.postcode]
    .filter(Boolean).join('\n')
}

const CONTACT_FIELDS: ContactField[] = ['email_for_inquiries', 'email_for_notifications', 'phone_for_inquiries', 'phone_for_notifications']

export function splitCommaSeparated(value?: string | null): string[] {
  return compact((value ?? '').split(',').map(v => v.trim()))
}

// Converts only the contact fields present on the form, so partial updates never send []
export function toContactLists<T>(form: WithContactStrings<T>): T {
  const result: any = { ...form }
  CONTACT_FIELDS.forEach(field => {
    if (form[field] !== undefined) result[field] = splitCommaSeparated(form[field])
  })
  return result
}

export function buildURLSearchParams(data: any) {
  const params = new URLSearchParams()
  forOwn(data, (value, key) => {
    if (value === undefined || value === '') {
      return
    } else if (isArrayLikeObject(value)) {
      forEach(value, (item) => {
        params.append(key, item)
      })
    } else {
      params.append(key, value)
    }
  })
  return params
}

export function approvalBooleanToString(value?: boolean | null) {
  if (value === undefined || value === null) {
    return 'not-set'
  } else if (value) {
    return 'approved'
  } else {
    return 'disapproved'
  }
}

export function approvalStringToBoolean(value: string) {
  if (value === 'approved') {
    return true
  } else if (value === 'disapproved') {
    return false
  } else {
    return null
  }
}

export function fromCentsToPounds(value?: string | number) {
  if (value === undefined || value === '') return undefined
  return Number(value) / 100
}

export function fromPountsToCents(value?: string | number) {
  if (value === undefined || value === '') return undefined
  return Number(value) * 100
}

export function getDocumentTypesFromFileType(fileType: DocumentFileType, exclude: DocumentType[] = []): DocumentType[] {
  let retval: DocumentType[] = []
  if (fileType === 'gdc') {
    retval.push('gdc')
  } else if (fileType === 'indemnity_insurance') {
    retval.push('indemnity_insurance')
  } else if (fileType === 'immunizations') {
    retval.push('hepatitis_b_vaccination', 'hepatitis_c', 'tb', 'hiv')
  } else if (fileType === 'dbs') {
    retval.push('dbs', 'pvg')
  } else if (fileType === 'infection_control') {
    retval.push('infection_control')
  } else if (fileType === 'cpr') {
    retval.push('cpr')
  } else if (fileType === 'resume') {
    retval.push('resume')
  } else if (fileType === 'sedation_certificate') {
    retval.push('sedation_certificate')
  } else if (fileType === 'radiography_certificate') {
    retval.push('radiography_certificate')
  }
  return retval.filter(docType => !exclude.includes(docType))
}

export function getFileTypeFromDocumentType(docType: DocumentType): DocumentFileType {
  if (docType === 'gdc') {
    return 'gdc'
  } else if (docType === 'indemnity_insurance') {
    return 'indemnity_insurance'
  } else if (['hepatitis_b_vaccination', 'hepatitis_c', 'tb', 'hiv'].includes(docType)) {
    return 'immunizations'
  } else if (['dbs', 'pvg'].includes(docType)) {
    return 'dbs'
  } else if (docType === 'infection_control') {
    return 'infection_control'
  } else if (docType === 'cpr') {
    return 'cpr'
  } else if (docType === 'resume') {
    return 'resume'
  } else if (docType === 'sedation_certificate') {
    return 'sedation_certificate'
  } else { // radiography_certificate
    return 'radiography_certificate'
  }
}

export function floorDateTime(value: DateTime, minutes: number = 5) {
  const floored = value.startOf('hour').plus({ minutes: Math.floor(value.minute / minutes) * minutes });
  return floored
}

export function formatCurrencyAmount(
  amount: number,
  currency: string,
  numFractionDigits: number = 2
) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    maximumFractionDigits: numFractionDigits,
    minimumFractionDigits: numFractionDigits,
  }).format(amount)
}
